/* pub-slideshow.js — port of Publisher (Dawn) SliderComponent + SlideshowComponent.
   Publisher's slideshow motion is native CSS scroll-snap with scroll-behavior:smooth
   plus manual loop jumps — NOT a carousel library. This reproduces that model.
   Scoped to .pub-slideshow roots. Idempotent, Theme Editor safe, teardown safe. */
(function () {
  if (window.__pubSlideshowLoaded) return;
  window.__pubSlideshowLoaded = true;

  var instances = new WeakMap();

  function Slideshow(root) {
    this.root = root;
    this.slider = root.querySelector('.pub-slider');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('.slideshow__slide'));
    if (!this.slider || this.slides.length === 0) return;

    this.controls = root.querySelector('.slideshow__controls');
    this.prevButton = root.querySelector('.slider-button--prev');
    this.nextButton = root.querySelector('.slider-button--next');
    this.currentEl = root.querySelector('.slider-counter--current');
    this.links = Array.prototype.slice.call(root.querySelectorAll('.slider-counter__link'));
    this.autoplayButton = root.querySelector('.slideshow__autoplay');
    this.liveRegion = root.querySelector('[data-pub-live]');

    this.currentPage = 1;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplaySpeed = (parseFloat(this.slider.dataset.speed) || 5) * 1000;
    this.autoplayEnabled = this.slider.dataset.autoplay === 'true';
    this.autoplayButtonIsSetToPlay = true;
    this._handlers = [];

    this._bind();
    this.update();
    if (this.autoplayEnabled) this.setAutoPlay();
  }

  Slideshow.prototype._on = function (el, type, fn, opts) {
    if (!el) return;
    el.addEventListener(type, fn, opts);
    this._handlers.push([el, type, fn, opts]);
  };

  Slideshow.prototype._bind = function () {
    var self = this;
    this._on(this.slider, 'scroll', throttle(function () { self.update(); }), { passive: true });
    this._on(this.prevButton, 'click', function (e) { e.preventDefault(); self.step(-1); });
    this._on(this.nextButton, 'click', function (e) { e.preventDefault(); self.step(1); });
    this.links.forEach(function (link, i) {
      self._on(link, 'click', function (e) { e.preventDefault(); self.goTo(i + 1); });
    });
  };

  Slideshow.prototype.slideWidth = function () {
    return this.slides[0].clientWidth;
  };

  Slideshow.prototype.step = function (dir) {
    var last = this.slides.length;
    var target = this.currentPage + dir;
    // Dawn's enableSliderLooping: jump to the opposite end at the boundaries
    if (target < 1) target = last;
    if (target > last) target = 1;
    this.goTo(target);
  };

  Slideshow.prototype.goTo = function (page) {
    this.currentPage = page;
    // Dawn scrolls the track and lets the scroll handler re-sync state. Painting
    // the UI from the *intended* page (not from scrollLeft, which has not moved
    // yet under scroll-behavior:smooth) keeps the counter/dots correct.
    this.slider.scrollTo({
      left: this.slideWidth() * (page - 1),
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
    });
    this.syncUI();
  };

  // recompute the current page from the real scroll position (scroll handler)
  Slideshow.prototype.update = function () {
    var w = this.slideWidth();
    if (w > 0) this.currentPage = Math.round(this.slider.scrollLeft / w) + 1;
    if (this.currentPage < 1) this.currentPage = 1;
    if (this.currentPage > this.slides.length) this.currentPage = this.slides.length;
    this.syncUI();
  };

  // paint controls from this.currentPage
  Slideshow.prototype.syncUI = function () {
    if (this.currentEl) this.currentEl.textContent = this.currentPage;
    this.links.forEach(function (link, i) {
      var active = i === this.currentPage - 1;
      link.classList.toggle('slider-counter__link--active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }, this);
    // Dawn removes the disabled state on a looping slideshow
    if (this.prevButton) this.prevButton.removeAttribute('disabled');
    if (this.nextButton) this.nextButton.removeAttribute('disabled');
    this.setSlideVisibility();
  };

  Slideshow.prototype.setSlideVisibility = function () {
    this.slides.forEach(function (slide, i) {
      var visible = i === this.currentPage - 1;
      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
      slide.querySelectorAll('a, button').forEach(function (el) {
        if (visible) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    }, this);
    if (this.liveRegion) {
      this.liveRegion.textContent = 'Slide ' + this.currentPage + ' of ' + this.slides.length;
    }
  };

  Slideshow.prototype.setAutoPlay = function () {
    var self = this;
    this._on(this.root, 'mouseover', function () { self.focusIn(); });
    this._on(this.root, 'mouseleave', function () { self.focusOut(); });
    this._on(this.root, 'focusin', function (e) { self.focusIn(e); });
    this._on(this.root, 'focusout', function (e) { self.focusOut(e); });

    if (this.autoplayButton) {
      this._on(this.autoplayButton, 'click', function () { self.toggle(); });
      this.autoplayButtonIsSetToPlay = !this.reducedMotion.matches;
      if (this.reducedMotion.matches) { this.pause(); this.reflectButton(false); }
      else this.play();
    } else {
      this.reducedMotion.matches ? this.pause() : this.play();
    }
  };

  Slideshow.prototype.play = function () {
    var self = this;
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.timer);
    this.timer = setInterval(function () { self.step(1); }, this.autoplaySpeed);
  };

  Slideshow.prototype.pause = function () {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.timer);
  };

  Slideshow.prototype.reflectButton = function (playing) {
    if (!this.autoplayButton) return;
    this.autoplayButton.classList.toggle('slideshow__autoplay--paused', !playing);
    this.autoplayButton.setAttribute('aria-label', playing ? 'Pause slideshow' : 'Play slideshow');
  };

  Slideshow.prototype.toggle = function () {
    if (this.autoplayButtonIsSetToPlay) { this.pause(); this.reflectButton(false); }
    else { this.play(); this.reflectButton(true); }
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  };

  Slideshow.prototype.focusIn = function (event) {
    if (this.autoplayButton) {
      var onButton = event && (event.target === this.autoplayButton || this.autoplayButton.contains(event.target));
      if (onButton && this.autoplayButtonIsSetToPlay) this.play();
      else if (this.autoplayButtonIsSetToPlay) this.pause();
    } else {
      this.pause();
    }
  };

  Slideshow.prototype.focusOut = function (event) {
    if (this.autoplayButton) {
      var onButton = event && (event.target === this.autoplayButton || this.autoplayButton.contains(event.target));
      if (!this.autoplayButtonIsSetToPlay || onButton) return;
      this.play();
    } else if (!this.reducedMotion.matches) {
      this.play();
    }
  };

  Slideshow.prototype.destroy = function () {
    clearInterval(this.timer);
    this._handlers.forEach(function (h) { h[0].removeEventListener(h[1], h[2], h[3]); });
    this._handlers = [];
  };

  function throttle(fn) {
    var wait = false;
    return function () {
      if (wait) return;
      fn.apply(this, arguments);
      wait = true;
      window.requestAnimationFrame(function () { wait = false; });
    };
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('.pub-slideshow');
    Array.prototype.forEach.call(roots, function (root) {
      if (instances.has(root)) return;          // never double-initialise
      var inst = new Slideshow(root);
      if (inst.slider) instances.set(root, inst);
    });
  }

  function teardown(scope) {
    var roots = (scope || document).querySelectorAll('.pub-slideshow');
    Array.prototype.forEach.call(roots, function (root) {
      var inst = instances.get(root);
      if (inst) { inst.destroy(); instances.delete(root); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  window.addEventListener('load', function () { init(document); });

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest ? e.target.closest('.pub-slideshow') : null;
    if (!root) return;
    var inst = instances.get(root);
    if (!inst) return;
    var idx = inst.slides.indexOf(e.target.closest('.slideshow__slide'));
    if (idx >= 0) { inst.pause(); inst.goTo(idx + 1); }
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var root = e.target.closest ? e.target.closest('.pub-slideshow') : null;
    if (!root) return;
    var inst = instances.get(root);
    if (inst && inst.autoplayEnabled && inst.autoplayButtonIsSetToPlay) inst.play();
  });

  window.__pubSlideshowInit = init;
})();
